/**
 * ----------------------------------------------------------------------------
 * CalloutPopoverContent
 * ----------------------------------------------------------------------------
 *
 * CalloutPopoverContent is a stylized body of content for CalloutPopoverBridge that supports
 * text, title, and image. * Content can be large or small with text adjusting accordingly.
 *
 * Options are available to include left, right, and/or close buttons.
 *
 */

import * as React from 'react';
import {noop} from 'lodash';

import {
  styled,
  styledDiv,
  useTheme,
  emotionStyled,
} from '@packages/utilities/styled';

import {
  Button as SpringButton,
  CloseDefaultIcon,
  IconButton,
  Text,
  View,
} from '@packages/systems/spring';

import Escapable from '@packages/systems/design-system/Escapable';
import KeyboardContext from '@packages/systems/design-system/KeyboardContext';
import {FinePrint, Copy} from '@packages/systems/design-system/Text';
import {Video} from '@packages/systems/design-system/Video';

import {CircleCheckSmall as CheckIcon} from '@packages/systems/ui/icons';

import useHitarea from '@packages/systems/ui/hooks/useHitarea';
import {useCollisionDetection} from '@packages/systems/notifications-atc';
import {
  IPN_IMPLEMENTATION,
  IPN_SURFACE,
  IPN_SLOT,
  IPN_TYPE,
} from '@packages/systems/telemetry';
import ReactDOMServer from 'react-dom/server';

import type {
  CalloutPopoverContentProps,
  CalloutPopoverButtonProps,
  CalloutPopoverButtonsProps,
  MessageType,
  ImageType,
  PopoverSizeType,
  CalloutPopoverGridStyleProps,
} from './types';

const atcEntryBase = {
  surface: IPN_SURFACE.DESIGNER,
  slot: IPN_SLOT.UNKNOWN, // TODO: Slot needs to be passed in via callers of CalloutPopoverContent
  implementation: IPN_IMPLEMENTATION.CALLOUT_POPOVER, // TODO: Implementation needs to be passed in via callers of CalloutPopoverContent
  ipnType: IPN_TYPE.POPOVER,
  feature: undefined, // TODO: Feature needs to be passed in via callers of CalloutPopoverContent
};

const POPOVER_WIDTHS = {
  LARGE: 430,
  MEDIUM: 360,
  SMALL: 200,
} as const;

const ASSET_HEIGHTS = {
  LARGE: 228,
  MEDIUM: 190,
  SMALL: 100,
} as const;

const ASSET_WIDTHS = {
  LARGE: 406, // POPOVER_WIDTHS.LARGE - 24px (paddingLeft + paddingRight)
  MEDIUM: 344, // POPOVER_WIDTHS.MEDIUM - 16px (paddingLeft + paddingRight)
  SMALL: 184, // POPOVER_WIDTHS.SMALL - 16px (paddingLeft + paddingRight)
} as const;

const SVG_OFFSET = 1;
const CHECK_BORDER_SIZE = 2;

const justifyStart = {justifySelf: 'start', gridColumn: 1} as const;
const justifyEnd = {justifySelf: 'end', gridColumn: 2} as const;

export const CalloutPopoverContent = React.memo<CalloutPopoverContentProps>(
  function PopoverContent({
    title,
    content,
    message,
    image,
    video,
    buttonLeft,
    buttonRight,
    buttonsContainerStyle,
    hasImageInsideContent,
    size,
    width,
    showCloseBtn,
    'data-automation-id': dataAutomationId,
    closePopover,
    buttonFooterText,
  }: CalloutPopoverContentProps) {
    const hasButtons = Boolean(buttonLeft || buttonRight);
    const hasCloseButton = Boolean(showCloseBtn && closePopover);
    const {colors} = useTheme();
    const white = {color: colors.white} as const;

    const messageBody = [
      title ? ReactDOMServer.renderToString(title) : '',
      content ? ReactDOMServer.renderToString(content) : '',
      message?.text,
    ]
      .filter(Boolean)
      .join(' ');

    const atcEntry = React.useMemo(
      () => ({
        ...atcEntryBase,
        messageBody,
        campaignId: dataAutomationId || 'callout-popover',
      }),
      [dataAutomationId, messageBody]
    );
    const containerRef = React.useRef<HTMLDivElement>(null);

    const {register, unregister} = useCollisionDetection();
    const atcId = React.useRef<string | null>(null);
    React.useEffect(() => {
      atcId.current = register({
        ...atcEntry,
        domRect: containerRef.current?.getBoundingClientRect(),
      });
      return () => {
        if (atcId.current) {
          unregister(atcId.current);
        }
      };
    }, [atcEntry, register, unregister]);

    const PopoverCopy = size === 'LARGE' ? Copy : FinePrint;
    return (
      <KeyboardContext>
        <Escapable
          onEscape={showCloseBtn && closePopover ? closePopover : noop}
        >
          <Grid
            ref={containerRef}
            data-copy-plain-text-selection
            data-automation-id={dataAutomationId}
            hasButtons={hasButtons}
            hasContent={Boolean(content)}
            hasTitle={Boolean(title)}
            hasMessage={Boolean(message)}
            hasCloseButton={hasCloseButton}
            messagePosition={message && message.position}
            hasImage={Boolean(image)}
            hasVideo={Boolean(video)}
            hasImageInsideContent={hasImageInsideContent}
            size={size}
            width={width}
          >
            {message && <Message {...message} size={size} />}
            {image && <PopoverImage {...image} size={size} />}
            {video && (
              <PopoverVideo
                src={video.url}
                videoTitle={video.title}
                autoPlay={video.autoPlay}
                size={size}
              />
            )}
            {title && (
              <Text
                font="title"
                weight="semibold"
                // @SPRING_CLEANING: add inverse text and actual bold
                color="$white"
                style={{fontWeight: 600}}
              >
                {title}
              </Text>
            )}
            {content && (
              <Content>
                <PopoverCopy style={white}>{content}</PopoverCopy>
              </Content>
            )}
            {hasCloseButton && closePopover && (
              <CalloutPopoverCloseBtn closePopover={closePopover} />
            )}
            {hasButtons ? (
              <Buttons style={buttonsContainerStyle}>
                {buttonLeft ? (
                  <CustomButton
                    ui={buttonLeft}
                    style={justifyStart}
                    size={size}
                  />
                ) : null}

                {buttonFooterText ? (
                  <Text color="$white" alignItems="center" display="flex">
                    {buttonFooterText}
                  </Text>
                ) : null}

                {buttonRight ? (
                  <CustomButton
                    ui={buttonRight}
                    style={justifyEnd}
                    size={size}
                  />
                ) : null}
              </Buttons>
            ) : null}
          </Grid>
        </Escapable>
      </KeyboardContext>
    );
  }
);

const Grid = styledDiv<CalloutPopoverGridStyleProps>(
  ({
    theme: {gutters, control},
    hasButtons,
    hasTitle,
    hasContent,
    hasMessage,
    hasImage,
    hasVideo,
    hasCloseButton,
    size = 'MEDIUM',
    width,
    messagePosition = 'top',
    hasImageInsideContent = false,
  }) => ({
    display: 'grid',
    userSelect: 'text',
    maxWidth: width ?? POPOVER_WIDTHS[size],
    borderRadius: control.borderRadius,
    padding:
      size === 'LARGE'
        ? `${gutters.base * 4}px ${gutters.narrow * 2}px`
        : `${gutters.wide}px ${gutters.base * 2}px`,
    gridGap: gutters.base * 2,
    gridTemplateColumns: 'auto 13px', // 13px is the size of the cross icon
    gridTemplateAreas: `
      ${hasMessage && messagePosition === 'top' ? '"message message"' : ''}
      ${hasImage ? '"image image"' : ''}
      ${hasVideo ? '"video video"' : ''}
      ${
        hasTitle
          ? hasCloseButton
            ? '"title closeButton"'
            : '"title title"'
          : ''
      }
      ${
        hasContent
          ? hasCloseButton
            ? hasImageInsideContent
              ? "'content content'"
              : '"content closeButton"'
            : '"content content"'
          : ''
      }
      ${hasMessage && messagePosition === 'bottom' ? '"message message"' : ''}
      ${hasButtons ? '"buttons buttons"' : ''}
    `,
  })
);

const StyledCheckIcon = styled(CheckIcon)(({theme: {gutters, colors}}) => ({
  width: gutters.x20,
  height: gutters.x20,
  marginRight: gutters.base - SVG_OFFSET,
  marginLeft: -SVG_OFFSET,
  color: colors.successBackground,
  zIndex: 1,
}));

const CheckIconBg = styledDiv(({theme: {colors, gutters}}) => ({
  position: 'absolute',
  left: gutters.base + SVG_OFFSET,
  width: gutters.x16,
  height: gutters.x16,
  backgroundColor: colors.white,
  borderRadius: 50,
}));

const ProgressCircle = styledDiv(
  ({theme: {colors, gutters}}) => ({
    width: gutters.x12 - CHECK_BORDER_SIZE,
    height: gutters.x12 - CHECK_BORDER_SIZE,
    border: `solid ${CHECK_BORDER_SIZE}px ${colors.backgroundLightTransparent}`,
    borderRadius: 50,
    marginLeft: gutters.x4,
  }),
  {
    success: ({theme: {colors, gutters}}) => ({
      borderColor: colors.successBright,
      marginRight: gutters.base,
      marginLeft: 0,
    }),
    full: ({theme: {colors}}) => ({
      borderColor: colors.white,
      backgroundColor: colors.white,
    }),
    active: ({theme: {colors}}) => ({
      borderColor: colors.white,
    }),
  }
);

// @ts-expect-error - TS7031 - Binding element 'total' implicitly has an 'any' type. | TS7031 - Binding element 'completed' implicitly has an 'any' type.
const ProgressIndicator = ({total, completed}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      marginLeft: 'auto',
    }}
  >
    {[...Array(total).keys()].map((i) => (
      <ProgressCircle
        key={i}
        full={i <= completed - 1}
        active={i === completed}
      />
    ))}
  </div>
);

const Message = ({text, check, progress, size = 'MEDIUM'}: MessageType) => {
  const {gutters, colors} = useTheme();
  const TextComponent = size === 'LARGE' ? Copy : FinePrint;
  return (
    <div
      style={{
        gridArea: 'message',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        height: 30,
        borderRadius: 3,
        backgroundColor: colors.backgroundDarkTransparent,
        color: colors.white,
        paddingLeft: gutters.base,
        paddingRight: gutters.base,
      }}
    >
      {check === false && <ProgressCircle success />}
      {check && (
        <>
          <CheckIconBg /> <StyledCheckIcon />
        </>
      )}
      <TextComponent white>
        {/* @ts-expect-error - TS2349 - This expression is not callable. */}
        {typeof text === 'function' ? text() : text}
      </TextComponent>
      {progress && <ProgressIndicator {...progress} />}
    </div>
  );
};

const ImageWrapper = emotionStyled(View)(
  {
    gridArea: 'image',
    width: '100%',
  },
  {
    variants: {
      size: {
        LARGE: {
          width: ASSET_WIDTHS.LARGE,
          height: ASSET_HEIGHTS.LARGE,
        },
        MEDIUM: {
          width: ASSET_WIDTHS.MEDIUM,
          height: ASSET_HEIGHTS.MEDIUM,
        },
        SMALL: {
          width: ASSET_WIDTHS.SMALL,
          height: ASSET_HEIGHTS.SMALL,
        },
      },
    },
  }
);

const ImageView = emotionStyled('img')({
  height: 'auto',
  width: '100%',
});

const PopoverImage = ({
  alt,
  height,
  size,
  src,
}: ImageType & {size?: PopoverSizeType}) => (
  <ImageWrapper style={{height}} size={size}>
    <ImageView alt={alt} src={src} />
  </ImageWrapper>
);

const PopoverVideo = ({
  size = 'MEDIUM',
  // @ts-expect-error - TS7031 - Binding element 'src' implicitly has an 'any' type.
  src,
  videoTitle = '',
  autoPlay = true,
}) => (
  <div
    style={{
      gridArea: 'video',
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly LARGE: 390; readonly MEDIUM: 340; readonly SMALL: 176; }'.
      width: ASSET_WIDTHS[size],
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly LARGE: 220; readonly MEDIUM: 190; readonly SMALL: 99; }'.
      height: ASSET_HEIGHTS[size],
    }}
  >
    <Video
      srcMp4={src}
      altText={videoTitle}
      autoPlay={autoPlay}
      loop={false}
      muted
      showControls="onFinish"
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly LARGE: 220; readonly MEDIUM: 190; readonly SMALL: 99; }'.
      style={{maxHeight: ASSET_HEIGHTS[size]}}
    />
  </div>
);

const Content = emotionStyled('div')({gridArea: 'content'});

const Buttons = ({style, children}: CalloutPopoverButtonsProps) => {
  const {gutters} = useTheme();
  return (
    <div
      style={{
        gridArea: 'buttons',
        display: 'grid',
        gridTemplateColumns: 'auto auto',
        gridGap: gutters.base * 2,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

const CustomButton = ({ui, style}: CalloutPopoverButtonProps) => {
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const [hitarea, handlers] = useHitarea();

  const baseStyles = {
    ...style,
    borderStyle: 'none',
  } as const;

  // @ts-expect-error - TS2339 - Property 'style' does not exist on type 'ButtonType'. | TS2339 - Property 'style' does not exist on type 'ButtonType'.
  const buttonStyle = ui.style ? {...baseStyles, ...ui.style} : baseStyles;

  if (ui.type === 'custom') {
    const RenderButton = ui.render;
    return (
      <RenderButton style={buttonStyle} hitarea={hitarea} handlers={handlers} />
    );
  }

  return (
    /**
     * CalloutPopover always renders on a blue background regardless of theme (e.g. darkDefault, light, etc.),
     * so all buttons rendered inside it must use colorVariant="white" to remain legible.
     */
    <SpringButton
      colorVariant="white"
      data-automation-id={ui['data-automation-id']}
      onClick={ui.onClick}
      style={buttonStyle}
      {...hitarea}
      {...handlers}
    >
      {ui.render}
    </SpringButton>
  );
};

const CloseBtnContainer = emotionStyled('div')({
  gridArea: 'closeButton',
});

const CalloutPopoverCloseBtn = ({
  closePopover,
}: {
  closePopover: (event?: React.SyntheticEvent<any>) => void;
}) => (
  <CloseBtnContainer>
    <IconButton
      Icon={CloseDefaultIcon}
      label="Close"
      onClick={closePopover}
      variant="ghost"
      colorVariant="white"
      density="compact"
      data-automation-id="callout-popover-close-btn"
    />
  </CloseBtnContainer>
);
